// Public Marketplace, Tenant Marketplace dashboard, Admin moderation
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Package, Store, Sparkles, ChevronRight, Plus, Trash2, TrendingUp, Eye, EyeOff, Heart, Check, X, Film, Ban, Star } from "lucide-react";
import { api, API, getToken } from "../lib/auth";

// ─────────── Public Marketplace homepage ───────────
export function MarketplaceHome() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("trending");
  const reload = () => {
    setData(null);
    const params = new URLSearchParams(); if (q) params.append("q", q); if (sort) params.append("sort", sort);
    axios.get(`${API}/public/marketplace?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData({ all: [] }));
  };
  useEffect(reload, [sort]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(reload, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  return (
    <div data-testid="market-home" className="min-h-screen bg-[#FFFCF2]">
      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between mb-7">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1B4332] to-[#52B788] flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h12a4 4 0 010 8H4V6z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
                <path d="M4 14h14a4 4 0 010 8H4v-8z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-extrabold text-2xl text-black">Brivo Marketplace</span>
          </Link>
          <Link to="/register" className="text-sm font-bold text-[#1B4332]">Sell with us →</Link>
        </div>

        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-[#1B4332] via-[#16834D] to-[#1FA868] text-white p-8 lg:p-12 mb-7 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight max-w-2xl">Discover products from independent African businesses.</h1>
          <p className="mt-3 text-white/85 max-w-xl">Thousands of products from verified Brivo merchants — shop fast, support local.</p>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-black/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="market-search"
            placeholder="Search products, stores, categories…"
            className="w-full pl-11 pr-4 py-4 rounded-2xl border border-black/10 bg-white text-base focus:border-[#1FA868] outline-none" />
        </div>
        <div className="flex gap-2 mb-7 overflow-x-auto pb-1">
          {[["trending", "🔥 Trending"], ["newest", "🆕 Newest"], ["price_low", "₦ Low → High"], ["price_high", "₦ High → Low"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setSort(k)} data-testid={`market-sort-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap ${sort === k ? "bg-[#1B4332] text-white" : "bg-white text-black/65 border border-black/10"}`}>
              {lbl}
            </button>
          ))}
        </div>

        {data === null && <div className="text-center py-16 text-black/45">Loading…</div>}

        {/* Featured Reel of the Day */}
        {data?.featured_reel && (
          <Section title="⭐ Featured Reel of the Day">
            <Link to="/m/guest/reels" data-testid="market-featured-reel"
              className="block bg-gradient-to-br from-[#1B4332] via-[#16834D] to-[#1FA868] rounded-3xl overflow-hidden relative aspect-[16/7] md:aspect-[16/5]">
              {data.featured_reel.thumbnail_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${data.featured_reel.thumbnail_url}`} alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-70" />
              ) : (
                <video src={`${process.env.REACT_APP_BACKEND_URL}${data.featured_reel.video_url}`}
                  className="absolute inset-0 w-full h-full object-cover opacity-70" muted autoPlay loop playsInline />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-7 lg:p-10 text-white max-w-2xl">
                <span className="self-start inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-400 text-amber-900 text-[10px] font-extrabold uppercase tracking-widest">
                  <Sparkles size={11} /> Featured today
                </span>
                <div className="mt-3 font-display font-extrabold text-2xl md:text-3xl line-clamp-2">{data.featured_reel.caption || data.featured_reel.tenant_name}</div>
                <div className="mt-1 text-sm font-bold opacity-90">by {data.featured_reel.tenant_name}</div>
                <div className="mt-3 text-xs opacity-85">{(data.featured_reel.view_count || 0).toLocaleString()} views · {(data.featured_reel.like_count || 0).toLocaleString()} likes</div>
                <div className="mt-4 inline-flex w-fit items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-[#1B4332] font-extrabold text-sm">
                  <Film size={14} /> Watch reel
                </div>
              </div>
            </Link>
          </Section>
        )}

        {data?.promoted?.length > 0 && (
          <Section title="✨ Promoted">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.promoted.map((l) => <ProductCard key={l.id} l={l} />)}
            </div>
          </Section>
        )}

        {data?.top_stores?.length > 0 && (
          <Section title="🏪 Top stores">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {data.top_stores.map((s) => (
                <Link key={s.subdomain} to={`/store/${s.subdomain}`} className="flex flex-col items-center gap-2 group">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white flex items-center justify-center font-extrabold text-3xl group-hover:scale-105 transition-transform">
                    {(s.name || "S")[0]}
                  </div>
                  <div className="text-xs font-bold text-black text-center">{s.name}</div>
                  <div className="text-[10px] text-black/55">{s.listings} items</div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        <Section title="🔥 Trending now">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(data?.trending || []).map((l) => <ProductCard key={l.id} l={l} />)}
          </div>
        </Section>

        {data?.new_arrivals?.length > 0 && (
          <Section title="🆕 New arrivals">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.new_arrivals.map((l) => <ProductCard key={l.id} l={l} />)}
            </div>
          </Section>
        )}

        {data?.all?.length === 0 && (
          <div className="bg-white rounded-3xl border border-dashed border-black/12 p-16 text-center">
            <Store size={36} className="mx-auto text-black/25 mb-3" />
            <div className="font-bold text-black text-lg">Marketplace is opening soon</div>
            <div className="text-sm text-black/55 mt-1">Subscribe to the Marketplace plan to publish your products here.</div>
            <Link to="/pricing" className="mt-5 inline-flex px-6 py-3 rounded-xl bg-[#1B4332] text-white font-bold text-sm">View plans</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-9">
      <h2 className="font-display font-extrabold text-2xl text-black mb-4">{title}</h2>
      {children}
    </section>
  );
}

function ProductCard({ l }) {
  return (
    <Link to={`/store/${l.tenant_subdomain}`} data-testid={`market-card-${l.id}`}
      className="bg-white rounded-2xl border border-black/8 overflow-hidden hover:shadow-md transition-shadow group">
      <div className="aspect-square bg-[#FAFBFB] flex items-center justify-center relative">
        {l.image_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image_url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <Package size={36} className="text-black/15" />
        )}
        {l.promoted && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-amber-400 text-amber-900 text-[10px] font-extrabold uppercase tracking-wider">Promoted</span>
        )}
      </div>
      <div className="p-3">
        <div className="font-bold text-sm text-black line-clamp-1">{l.title}</div>
        <div className="text-[11px] text-black/55 line-clamp-1">{l.tenant_name}</div>
        <div className="mt-1.5 font-extrabold text-lg text-[#1FA868]">₦{Number(l.price || 0).toLocaleString()}</div>
      </div>
    </Link>
  );
}

// ─────────── Tenant Marketplace dashboard ───────────
export function TenantMarketplace() {
  const [listings, setListings] = useState(null);
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const reload = () => {
    setListings(null);
    api.get("/t/marketplace/listings").then((r) => setListings(r.data || [])).catch(() => setListings([]));
    api.get("/t/products").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/t/marketplace/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(reload, []);

  const addListing = async (productId) => {
    try { await api.post("/t/marketplace/listings", { product_id: productId }); toast.success("Listed!"); setShowAdd(false); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (lid) => { try { await api.delete(`/t/marketplace/listings/${lid}`); toast.success("Unlisted"); reload(); } catch { toast.error("Failed"); } };
  const togglePromote = async (l) => {
    try { await api.patch(`/t/marketplace/listings/${l.id}`, { product_id: l.product_id, promoted: !l.promoted }); reload(); }
    catch { toast.error("Failed"); }
  };
  const listedIds = new Set((listings || []).map((l) => l.product_id));
  const available = products.filter((p) => !listedIds.has(p.id));

  return (
    <div data-testid="tenant-marketplace-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Marketplace</h1>
          <p className="text-sm text-black/55 mt-1">Publish products to the public marketplace.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="tm-add-listing"
          className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm inline-flex items-center gap-2 shadow">
          <Plus size={15} /> List product
        </button>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat lbl="Listings" v={analytics.listings_count} icon={Store} />
          <Stat lbl="Views" v={analytics.listings_views} icon={Eye} />
          <Stat lbl="Saves" v={analytics.listings_saves} icon={Heart} />
          <Stat lbl="Promoted" v={analytics.promoted_count} icon={Sparkles} tint="amber" />
        </div>
      )}

      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {listings === null ? (
          <div className="p-16 text-center text-black/45">Loading…</div>
        ) : listings.length === 0 ? (
          <div className="p-16 text-center">
            <Store size={36} className="mx-auto text-black/25 mb-2" />
            <div className="font-bold text-black">No listings yet</div>
            <div className="text-sm text-black/55 mt-1">List your first product to appear in the public marketplace.</div>
          </div>
        ) : (
          <div className="divide-y divide-black/6">
            {listings.map((l) => (
              <div key={l.id} className="flex items-center gap-4 p-4">
                <div className="w-14 h-14 rounded-xl bg-[#FAFBFB] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {l.product?.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${l.product.image_url}`} alt="" className="w-full h-full object-cover" /> : <Package size={20} className="text-black/20" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-black truncate">{l.title}</div>
                  <div className="text-xs text-black/55">₦{Number(l.product?.price || 0).toLocaleString()} · {l.views || 0} views · {l.saves || 0} saves</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${l.status === "approved" ? "bg-emerald-50 text-emerald-700" : l.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{l.status}</span>
                <button onClick={() => togglePromote(l)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold ${l.promoted ? "bg-amber-100 text-amber-800" : "bg-black/5 text-black/65 hover:bg-black/10"}`}>
                  {l.promoted ? "Promoted ★" : "Promote"}
                </button>
                <button onClick={() => remove(l.id)} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-black/8">
              <div className="font-display font-bold text-xl text-black">Pick a product to list</div>
              <button onClick={() => setShowAdd(false)} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {available.length === 0 ? (
                <div className="text-center text-sm text-black/55 py-10">All products are listed.</div>
              ) : available.map((p) => (
                <button key={p.id} onClick={() => addListing(p.id)} data-testid={`tm-list-${p.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-black/8 hover:border-[#1FA868] text-left transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-[#FAFBFB] flex items-center justify-center flex-shrink-0">
                    {p.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} alt="" className="w-full h-full object-cover rounded-xl" /> : <Package size={18} className="text-black/20" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-black truncate">{p.name}</div>
                    <div className="text-xs text-black/55">₦{Number(p.price || 0).toLocaleString()} · stock {p.stock || 0}</div>
                  </div>
                  <Plus size={14} className="text-[#1FA868]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ lbl, v, icon: Icon, tint = "emerald" }) {
  const map = { emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", sky: "bg-sky-50 text-sky-700" };
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-5">
      <div className={`w-10 h-10 rounded-xl ${map[tint]} flex items-center justify-center`}><Icon size={17} /></div>
      <div className="mt-3 text-xs font-bold uppercase tracking-wider text-black/55">{lbl}</div>
      <div className="font-display font-extrabold text-2xl text-black mt-1">{Number(v || 0).toLocaleString()}</div>
    </div>
  );
}

// ─────────── Master Admin: Marketplace Moderation Queue ───────────
export function AdminMarketplaceQueue() {
  const [listings, setListings] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [banModal, setBanModal] = useState(null); // { tenant_id, tenant_name, banned }
  const reload = () => {
    setListings(null);
    const params = filter !== "all" ? `?status=${filter}` : "";
    axios.get(`${API}/admin/marketplace/listings${params}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then((r) => setListings(r.data || [])).catch(() => setListings([]));
  };
  useEffect(reload, [filter]); // eslint-disable-line

  const moderate = async (lid, status) => {
    try {
      await axios.patch(`${API}/admin/marketplace/listings/${lid}/moderate`, { status },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(status === "approved" ? "Approved" : "Rejected");
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  const delist = async (lid) => {
    if (!window.confirm("Permanently delist this product from the marketplace?")) return;
    try {
      await axios.delete(`${API}/admin/marketplace/listings/${lid}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Listing delisted");
      reload();
    } catch { toast.error("Failed to delist"); }
  };

  const banTenant = async (tenantId, banned, reason) => {
    try {
      await axios.post(`${API}/admin/tenants/${tenantId}/marketplace-ban`,
        { banned, reason: reason || "" },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(banned ? "Tenant banned from marketplace" : "Tenant unbanned");
      setBanModal(null);
      reload();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-marketplace-queue" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Marketplace Moderation</h1>
          <p className="text-sm text-black/55 mt-1">Approve, reject, delist listings or ban tenants from posting.</p>
        </div>
      </div>
      <div className="flex gap-2 mb-5">
        {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`amq-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-bold ${filter === k ? "bg-[#1B4332] text-white" : "bg-white text-black/65 border border-black/10"}`}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {listings === null ? (
          <div className="p-14 text-center text-black/45">Loading…</div>
        ) : listings.length === 0 ? (
          <div className="p-14 text-center">
            <Sparkles size={28} className="mx-auto text-black/25 mb-2" />
            <div className="font-bold text-black">No listings to moderate</div>
            <div className="text-sm text-black/55 mt-1">Queue is clear.</div>
          </div>
        ) : (
          <div className="divide-y divide-black/6">
            {listings.map((l) => (
              <div key={l.id} className="flex items-center gap-4 p-4">
                <div className="w-14 h-14 rounded-xl bg-[#FAFBFB] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {l.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image_url}`} alt="" className="w-full h-full object-cover" /> : <Package size={20} className="text-black/20" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-black truncate">{l.title}</div>
                  <div className="text-xs text-black/55">By {l.tenant_name} · {l.tenant_subdomain} · ₦{Number(l.price || 0).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${l.status === "approved" ? "bg-emerald-50 text-emerald-700" : l.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{l.status}</span>
                <div className="flex gap-2">
                  <button onClick={() => moderate(l.id, "approved")} data-testid={`amq-approve-${l.id}`}
                    title="Approve"
                    className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold inline-flex items-center gap-1 hover:bg-emerald-100">
                    <Check size={12} /> Approve
                  </button>
                  <button onClick={() => moderate(l.id, "rejected")} data-testid={`amq-reject-${l.id}`}
                    title="Reject (unpublish)"
                    className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-xs font-bold inline-flex items-center gap-1 hover:bg-rose-100">
                    <X size={12} /> Reject
                  </button>
                  <button onClick={() => delist(l.id)} data-testid={`amq-delist-${l.id}`}
                    title="Delete permanently"
                    className="px-2 py-1.5 rounded-lg bg-black/5 text-black/65 hover:bg-rose-50 hover:text-rose-700">
                    <Trash2 size={13} />
                  </button>
                  <button onClick={() => setBanModal({ tenant_id: l.tenant_id, tenant_name: l.tenant_name })}
                    data-testid={`amq-ban-${l.tenant_id}`}
                    title="Ban tenant from marketplace"
                    className="px-2 py-1.5 rounded-lg bg-black/5 text-black/65 hover:bg-amber-50 hover:text-amber-700">
                    <Ban size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {banModal && (
        <BanTenantModal modal={banModal} kind="marketplace" onClose={() => setBanModal(null)}
          onConfirm={(reason) => banTenant(banModal.tenant_id, true, reason)} />
      )}
    </div>
  );
}

function BanTenantModal({ modal, kind, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center"><Ban size={18} /></div>
          <div>
            <div className="font-display font-bold text-xl text-black">Ban tenant from {kind}</div>
            <div className="text-xs text-black/55">{modal.tenant_name}</div>
          </div>
        </div>
        <p className="text-sm text-black/65 mb-3">
          This will immediately unpublish all their {kind === "reels" ? "reels" : "listings"} and prevent future posts. They can be unbanned later from the tenant detail page.
        </p>
        <label className="block">
          <span className="block text-xs font-bold uppercase tracking-wider text-black/55 mb-1.5">Reason (optional)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="e.g. Repeated guideline violations…"
            className="w-full px-3 py-2.5 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-rose-500" />
        </label>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-black/5 text-black/65 font-bold text-sm">Cancel</button>
          <button onClick={() => onConfirm(reason)} data-testid="ban-modal-confirm"
            className="px-4 py-2 rounded-xl bg-rose-600 text-white font-bold text-sm">Ban tenant</button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Master Admin: Reels Moderation ───────────
export function AdminReelsQueue() {
  const [reels, setReels] = useState(null);
  const [filter, setFilter] = useState("approved");
  const [banModal, setBanModal] = useState(null);
  const reload = () => {
    setReels(null);
    const params = filter !== "all" ? `?status=${filter}` : "";
    axios.get(`${API}/admin/reels${params}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setReels(r.data || [])).catch(() => setReels([]));
  };
  useEffect(reload, [filter]); // eslint-disable-line

  const moderate = async (rid, status) => {
    try {
      await axios.patch(`${API}/admin/reels/${rid}/moderate`, { status },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(status === "rejected" ? "Reel unpublished" : "Reel approved");
      reload();
    } catch { toast.error("Failed"); }
  };

  const delReel = async (rid) => {
    if (!window.confirm("Permanently delete this reel and all its comments/likes?")) return;
    try {
      await axios.delete(`${API}/admin/reels/${rid}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Reel deleted");
      reload();
    } catch { toast.error("Failed"); }
  };

  const toggleFeatured = async (rid, makeFeatured) => {
    try {
      await axios.patch(`${API}/admin/reels/${rid}/feature`, { featured: makeFeatured },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(makeFeatured ? "Featured on marketplace homepage" : "Unfeatured");
      reload();
    } catch { toast.error("Failed"); }
  };

  const banTenant = async (tenantId, banned, reason) => {
    try {
      await axios.post(`${API}/admin/tenants/${tenantId}/reels-ban`,
        { banned, reason: reason || "" },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(banned ? "Tenant banned from reels" : "Tenant unbanned");
      setBanModal(null);
      reload();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-reels-queue" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Reels Moderation</h1>
          <p className="text-sm text-black/55 mt-1">Unpublish, delete reels or ban tenants from posting.</p>
        </div>
      </div>
      <div className="flex gap-2 mb-5">
        {[["approved", "Live"], ["pending", "Pending"], ["rejected", "Unpublished"], ["all", "All"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`arq-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-bold ${filter === k ? "bg-[#1B4332] text-white" : "bg-white text-black/65 border border-black/10"}`}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {reels === null ? (
          <div className="col-span-full p-14 text-center text-black/45">Loading…</div>
        ) : reels.length === 0 ? (
          <div className="col-span-full bg-white rounded-2xl border border-dashed border-black/12 p-14 text-center">
            <Film size={28} className="mx-auto text-black/25 mb-2" />
            <div className="font-bold text-black">No reels in this filter</div>
          </div>
        ) : (
          reels.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-black/8 overflow-hidden">
              <div className="aspect-[9/16] bg-black relative">
                {r.thumbnail_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${r.thumbnail_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <video src={`${process.env.REACT_APP_BACKEND_URL}${r.video_url}`} className="w-full h-full object-cover" muted />
                )}
                <span className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${r.moderation_status === "approved" ? "bg-emerald-500 text-white" : r.moderation_status === "rejected" ? "bg-rose-500 text-white" : "bg-amber-500 text-white"}`}>
                  {r.moderation_status}
                </span>
                {r.featured && (
                  <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400 text-amber-900 inline-flex items-center gap-0.5">
                    <Star size={9} fill="currentColor" /> Featured
                  </span>
                )}
                <div className="absolute bottom-2 left-2 right-2 text-white text-xs">
                  <div className="font-bold truncate drop-shadow">{r.tenant_name}</div>
                  <div className="opacity-85 truncate drop-shadow">{r.caption || "—"}</div>
                </div>
              </div>
              <div className="p-2.5 grid grid-cols-2 gap-1.5">
                {r.moderation_status !== "rejected" ? (
                  <button onClick={() => moderate(r.id, "rejected")} data-testid={`arq-unpublish-${r.id}`}
                    className="px-2 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-[11px] font-bold inline-flex items-center justify-center gap-1">
                    <EyeOff size={11} /> Unpublish
                  </button>
                ) : (
                  <button onClick={() => moderate(r.id, "approved")} data-testid={`arq-republish-${r.id}`}
                    className="px-2 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] font-bold inline-flex items-center justify-center gap-1">
                    <Check size={11} /> Republish
                  </button>
                )}
                <button onClick={() => delReel(r.id)} data-testid={`arq-delete-${r.id}`}
                  className="px-2 py-1.5 rounded-lg bg-black/5 text-black/65 hover:bg-rose-50 hover:text-rose-700 text-[11px] font-bold inline-flex items-center justify-center gap-1">
                  <Trash2 size={11} /> Delete
                </button>
                <button onClick={() => setBanModal({ tenant_id: r.tenant_id, tenant_name: r.tenant_name })}
                  data-testid={`arq-ban-${r.tenant_id}`}
                  className="col-span-2 px-2 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 text-[11px] font-bold inline-flex items-center justify-center gap-1">
                  <Ban size={11} /> Ban this tenant from posting reels
                </button>
                <button onClick={() => toggleFeatured(r.id, !r.featured)} data-testid={`arq-feature-${r.id}`}
                  className={`col-span-2 px-2 py-1.5 rounded-lg text-[11px] font-bold inline-flex items-center justify-center gap-1 ${r.featured ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}>
                  <Star size={11} fill={r.featured ? "currentColor" : "none"} /> {r.featured ? "Unfeature" : "Feature on marketplace"}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {banModal && (
        <BanTenantModal modal={banModal} kind="reels" onClose={() => setBanModal(null)}
          onConfirm={(reason) => banTenant(banModal.tenant_id, true, reason)} />
      )}
    </div>
  );
}
